import { useQuery } from '@tanstack/react-query'
import { tenantQueries } from '@milliswift/api-client'
import {
  AreaTrendChart,
  BarSeriesChart,
  Callout,
  ChartCard,
  HistogramChart,
  Meter,
  PageSkeleton,
  PreviewDataBadge,
  SectionHeader,
  SegmentedControl,
} from '@milliswift/ui'
import { useState } from 'react'

/**
 * Usage analytics.
 *
 * Quota numbers are real (GET /account/quota). The time-series charts are
 * preview data — there is no historical usage endpoint yet
 * (docs/PLATFORM_API_GAPS.md).
 */

const RANGE_SERIES: Record<string, { label: string; rows: number }[]> = {
  '7d': Array.from({ length: 7 }, (_, i) => ({
    label: `Day ${i + 1}`,
    rows: Math.round(180e6 + Math.sin(i) * 40e6 + i * 12e6),
  })),
  '30d': Array.from({ length: 30 }, (_, i) => ({
    label: String(i + 1),
    rows: Math.round(180e6 + Math.sin(i / 3) * 60e6 + i * 4e6),
  })),
  '90d': Array.from({ length: 90 }, (_, i) => ({
    label: String(i + 1),
    rows: Math.round(160e6 + Math.sin(i / 9) * 80e6 + i * 1.2e6),
  })),
}

const LATENCY_BUCKETS = [
  { label: '<10ms', count: 4200, tone: 'success' as const },
  { label: '10–50ms', count: 8100, tone: 'success' as const },
  { label: '50–100ms', count: 2400, tone: 'warning' as const },
  { label: '100–500ms', count: 610, tone: 'warning' as const },
  { label: '500ms+', count: 92, tone: 'danger' as const },
]

export default function Usage() {
  const [range, setRange] = useState('30d')
  const quota = useQuery(tenantQueries.quota())

  if (quota.isPending) return <PageSkeleton />

  const q = quota.data
  const series = RANGE_SERIES[range] ?? RANGE_SERIES['30d']!

  // Stack the type chart properly rather than the broken draft above.
  const typeSeries = Array.from({ length: 7 }, (_, i) => ({
    label: `Day ${i + 1}`,
    select: 3800 + ((i * 137) % 500),
    write: 380 + ((i * 61) % 120),
  }))

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Usage"
        description="Rows scanned, storage and query patterns over time."
        action={<PreviewDataBadge />}
      />

      {q?.quota_exceeded_kind ? (
        <Callout tone="warning" title="Plan limit reached">
          {q.quota_exceeded_kind === 'rows'
            ? 'Row-scan limit exceeded for this period.'
            : 'Storage limit exceeded for this period.'}
        </Callout>
      ) : null}

      <SegmentedControl
        value={range}
        onValueChange={setRange}
        options={[
          { value: '7d', label: '7 days' },
          { value: '30d', label: '30 days' },
          { value: '90d', label: '90 days' },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Rows scanned" description="Per day · preview data">
          <AreaTrendChart
            data={series}
            series={[{ key: 'rows', name: 'Rows' }]}
          />
        </ChartCard>

        <ChartCard title="Query latency distribution" description="Share of queries by bucket · preview data">
          <HistogramChart data={LATENCY_BUCKETS} />
        </ChartCard>

        <ChartCard title="Queries by type" description="Reads versus writes · preview data">
          <BarSeriesChart
            data={typeSeries}
            series={[
              { key: 'select', name: 'SELECT' },
              { key: 'write', name: 'INSERT / UPDATE / DELETE', tone: 'info' },
            ]}
            stacked
          />
        </ChartCard>

        <div className="rounded-card border border-line bg-surface p-5 shadow-card">
          <h3 className="text-sm font-semibold text-foreground">This period</h3>
          <p className="mt-0.5 text-caption text-muted">
            Since {q ? new Date(q.period_start).toLocaleDateString() : '—'}
          </p>
          <div className="mt-5 space-y-5">
            <Meter
              value={q?.rows_percent_used ?? 0}
              max={100}
              label="Rows scanned"
              format={() =>
                `${(q?.rows_scanned ?? 0).toLocaleString()} · ${(q?.rows_percent_used ?? 0).toFixed(0)}%`
              }
            />
            <Meter
              value={q?.gb_stored ?? 0}
              max={Math.max((q?.gb_stored ?? 0) * 1.5, 10)}
              label="Storage"
              format={(v, m) => `${v.toFixed(1)} GB of ${m.toFixed(0)} GB`}
            />
            <Meter
              value={q?.gb_written ?? 0}
              max={Math.max((q?.gb_written ?? 0) * 2, 1)}
              label="Written"
              format={(v) => `${v.toFixed(1)} GB`}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
